import { defineStore } from 'pinia'
import { fetchAppInit } from '@/shared/services/app-init.api'
import { clearAllAuthStorage } from '@/modules/auth/services/auth.storage'

type AppInitStatus = 'idle' | 'loading' | 'ready' | 'error'

type Company = {
  name: string
  logoUrl?: string,
  publishable_key?: string
}

type ServiceGroup = Record<string, any>
type Vendor = Record<string, any>

let inFlightAppInit: Promise<void> | null = null

function isTokenAuthError(error: unknown) {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 401) return true
  if (status === 419) return true

  const rawMessage = [
    (error as { response?: { data?: { message?: unknown; error?: unknown } } })?.response?.data?.message,
    (error as { response?: { data?: { message?: unknown; error?: unknown } } })?.response?.data?.error,
    (error as { message?: unknown })?.message
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(' ')

  return /token|expired|unauthori[sz]ed|unauthenticated/.test(rawMessage)
}

function logoutAfterTokenFailure() {
  clearAllAuthStorage()
  if (typeof window === 'undefined') return

  const currentHash = window.location.hash.replace(/^#/, '') || '/'
  const isAuthRoute =
    currentHash.startsWith('/login') ||
    currentHash.startsWith('/signup') ||
    currentHash.startsWith('/reset-password')

  window.dispatchEvent(new Event('auth-changed'))

  if (!isAuthRoute) {
    const redirect = encodeURIComponent(currentHash)
    window.location.hash = `/login?redirect=${redirect}`
  }
}

export const useAppStore = defineStore('app', {
  state: () => ({
    appName: 'GlobalApp',
    tagline: 'Fresh setup on Vue 3, Vuetify 3, and Pinia.',
    company: { name: 'GlobalApp', logoUrl: '', publishable_key: '' } as Company,
    serviceGroups: [] as ServiceGroup[],
    topProviders: [] as Vendor[],
    status: 'idle' as AppInitStatus,
    errorMessage: ''
  }),

  actions: {
    async loadAppInit(force = false) {
      if (this.status === 'ready' && !force) return
      if (this.status === 'loading' && inFlightAppInit) return inFlightAppInit

      this.status = 'loading'
      this.errorMessage = ''

      try {
        inFlightAppInit = (async () => {
          const payload = await fetchAppInit()
          const company = payload?.company || {}
          const serviceGroups = Array.isArray(payload?.serviceGroups)
            ? payload.serviceGroups
            : []
          const vendorList = Array.isArray(payload?.vendorList)
            ? payload.vendorList
            : []

          this.company = {
            name: company.name || this.appName,
            logoUrl: company.logoUrl || '',
            publishable_key: company.publishable_key || ''
          }
          this.serviceGroups = serviceGroups
          this.topProviders = vendorList
          this.status = 'ready'
        })()
        await inFlightAppInit
      } catch (error) {
        console.error('Failed to load app init', error)

        if (isTokenAuthError(error)) {
          logoutAfterTokenFailure()
          this.status = 'idle'
          this.errorMessage = ''
          return
        }

        this.status = 'error'
        this.errorMessage = (error as Error)?.message || 'Failed to load app data'
      } finally {
        inFlightAppInit = null
      }
    }
  }
})
